<template>
  <div class="makeup-class-table">
    <div class="header">
      <h2>Makeup Classes for Session</h2>
    </div>

    <div v-if="loading" class="loading">Loading makeup classes...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="makeupClasses.length === 0" class="no-records">
      No makeup classes scheduled for this session
    </div>
    <div v-else>
      <div class="save-section">
        <button @click="saveAllChanges" class="save-btn" :disabled="isSaving">
          {{ isSaving ? 'Saving...' : 'Save All Changes' }}
        </button>
        <div class="completion-status">
          {{ completionStatusMessage }}
        </div>
      </div>
      <table class="makeup-table">
        <thead>
          <tr>
            <th>Student Name</th>
            <th>Status</th>
            <th>Makeup Class Date</th>
            <th>Start Time</th>
            <th>Duration</th>
            <th>Staff</th>
            <th>School</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="makeup in makeupClasses" :key="makeup.id">
            <td>{{ makeup.student.name }}</td>
            <td>
              <select v-model="makeup.status" class="status-select">
                <option v-for="status in makeupStatusOptions" :key="status" :value="status">
                  {{ status }}
                </option>
              </select>
            </td>
            <td>
              <input 
                type="date"
                v-model="makeup.classDate" 
                class="date-picker"
                @change="updateMakeupTimes(makeup)"
              >
            </td>
            <td>
              <input 
                type="time" 
                v-model="makeup.startTime" 
                class="time-picker"
                @change="updateMakeupTimes(makeup)"
              >
            </td>
            <td>
              <select 
                v-model="makeup.duration" 
                class="duration-select"
                @change="updateMakeupTimes(makeup)"
              >
                <option v-for="duration in durationOptions" :key="duration" :value="duration">
                  {{ duration }} min
                </option>
              </select>
            </td>
            <td>
              <select v-model="makeup.staff_id" class="staff-select">
                <option value="">-- Select Staff --</option>
                <option v-for="staff in staffList" :key="staff.staff_id" :value="staff.staff_id">
                  {{ staff.name }}
                </option>
              </select>
            </td>
            <td>
              <select v-model="makeup.school_id" class="school-select">
                <option value="">-- Select School --</option>
                <option v-for="school in schools" :key="school.school_id" :value="school.school_id">
                  {{ school.name }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { 
  GET_MAKEUP_CLASSES_BY_SESSION,
  BULK_UPDATE_MAKEUP_CLASS,
} from '../graphql/queries';

// Props
const props = defineProps({
  refreshKey: {
    type: Number,
    required: false
  }
});

const route = useRoute();
const session_id = parseInt(route.params.session_id);

// Component state
const makeupClasses = ref([]);
const staffList = ref([]);
const schools = ref([]);
const loading = ref(true);
const error = ref(null);
const isSaving = ref(false);

// Computed
const completionStatusMessage = computed(() => {
  const total = makeupClasses.value.length;
  const completed = makeupClasses.value.filter(mc => mc.status === 'COMPLETED').length;
  const remaining = total - completed;
  
  if (total === 0) return 'No makeup classes to track';
  if (remaining === 0) return 'All makeup classes completed! 🎉';
  
  return `${remaining} of ${total} students still need to complete makeup classes`;
});

// Options for selects
const makeupStatusOptions = ['NOTED', 'SCHEDULED', 'COMPLETED', 'CANCELLED'];
const durationOptions = [30, 60, 90, 120, 150, 180];

// Fetch initial data
const { onResult, onError, refetch } = useQuery(GET_MAKEUP_CLASSES_BY_SESSION, { session_id });
onResult((result) => {
  if (result.data) {
    staffList.value = result.data.staff;
    schools.value = result.data.schools;
    
    // Transform makeup classes for editing
    makeupClasses.value = result.data.makeupClassesBySession.map(mc => ({
      id: mc.id,
      student_id: mc.student_id,
      session_id: mc.session_id,
      school_id: mc.school_id || null,
      staff_id: mc.staff_id || null,
      status: mc.status,
      student: mc.student,
      classDate: mc.start_at ? formatDateForInput(mc.start_at) : '',
      startTime: mc.start_at ? formatTimeForInput(mc.start_at) : '',
      duration: mc.start_at && mc.end_at 
        ? Math.round((new Date(mc.end_at) - new Date(mc.start_at))) / (1000 * 60)
        : 90,
      originalData: mc // Keep original for comparison
    }));
  }
  loading.value = false;
});

onError((err) => {
  error.value = `Error loading makeup classes: ${err.message}`;
  loading.value = false;
});

// Parent component triggered refetch
watch(() => props.refreshKey, () => { 
  refetch();
});

// Mutation
const { mutate: updateMakeupClasses } = useMutation(BULK_UPDATE_MAKEUP_CLASS);

// Helper functions
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
}

function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
}

function formatTimeForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toTimeString().substring(0, 5);
}

function updateMakeupTimes(makeup) {
  if (makeup.classDate && makeup.startTime) {
    const [hours, minutes] = makeup.startTime.split(':').map(Number);
    const date = new Date(makeup.classDate);
    date.setHours(hours, minutes, 0, 0);
    
    makeup.start_at = date.toISOString();
    
    if (makeup.duration) {
      const endDate = new Date(date.getTime() + makeup.duration * 60000);
      makeup.end_at = endDate.toISOString();
    }
  }
}

// Save action
async function saveAllChanges() {
  isSaving.value = true;
  
  try {
    // Prepare inputs for mutation
    const inputs = makeupClasses.value.map(mc => {
      updateMakeupTimes(mc); // Ensure times are updated
      
      return {
        id: mc.id,
        student_id: mc.student_id,
        session_id: mc.session_id,
        school_id: mc.school_id || null,
        staff_id: mc.staff_id || null,
        status: mc.status,
        start_at: mc.start_at,
        end_at: mc.end_at
      };
    });
    
    const result = await updateMakeupClasses({ inputs });
    
    // Update local state with returned data
    if (result.data?.updateMakeupClasses) {
      makeupClasses.value = makeupClasses.value.map(mc => {
        const updated = result.data.updateMakeupClasses.find(m => m.id === mc.id);
        return updated ? { ...mc, originalData: updated } : mc;
      });
    }
    
    alert('Makeup classes updated successfully!');
  } catch (err) {
    alert(`Error updating makeup classes: ${err.message}`);
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped>
.makeup-class-table {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  margin-bottom: 20px;
}

.save-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 15px;
}

.completion-status {
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 0.9rem;
}

.save-btn {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 15px;
}

.save-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

.makeup-table {
  width: 100%;
  border-collapse: collapse;
}

.makeup-table th, .makeup-table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
}

.makeup-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.makeup-table tr:nth-child(even) {
  background-color: #f9f9f9;
}

.makeup-table tr:hover {
  background-color: #f0f0f0;
}

select, input {
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
}

.loading, .error, .no-records {
  padding: 20px;
  text-align: center;
}

.error {
  color: #f44336;
}
</style>